import { useDetailsResource } from 'framework/ViewTypes/hooks';
import { useParams } from 'react-router-dom';
import { ACTIVE_STATUS, useEmployeeService, useEmployeeSuggestionsService } from 'domain/shared/employee';
import { POSITION_FILL_REQUEST_STATUS } from '../../constants';
import type { PositionFillRequestListItem } from '../../types';
import usePositionFillRequestsService from '../../usePositionFillRequestsService';
import { CTO_POSITION_ID } from 'domain/shared/employee/position/positionService';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { UserName } from 'infrastructure/security/authentication/authenticatedUserService';

const getOwnersByStatus = async (
  status: PositionFillRequestListItem['status'],
  getFullNameWithoutMaiden: (filter: Nullable<Partial<UserName>> | undefined) => string,
  searchAll: (filter?: {}) => Promise<Dictionary<string>[]>,
  getLeadRecruiters?: () => Promise<any>,
) => {
  if (status !== POSITION_FILL_REQUEST_STATUS.REQUESTED && status !== POSITION_FILL_REQUEST_STATUS.PENDING) {
    return [];
  }

  const leadRecruiters = (await getLeadRecruiters?.()) ?? [];
  const owners = [...(leadRecruiters ?? [])];

  if (status === POSITION_FILL_REQUEST_STATUS.REQUESTED) {
    const ctoOwners = await searchAll({
      filter: {
        workingStatus: {
          in: [ACTIVE_STATUS],
        },
        'position.id': {
          in: [String(CTO_POSITION_ID)],
        },
      },
    });

    owners.push(...(ctoOwners ?? []));
  }

  return owners.map(item => ({
    id: item.alias ?? item.id,
    name: getFullNameWithoutMaiden(item?.nameEn),
  }));
};

const usePositionFillRequestDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { addMessage } = useMessageService();
  const { getById } = usePositionFillRequestsService();
  const { getLeadRecruiters, searchAll } = useEmployeeSuggestionsService();
  const { getFullNameWithoutMaiden } = useEmployeeService();
  const { data, ...state } = useDetailsResource(id, async requestId => {
    const positionFillRequest = await getById(requestId);
    if (!positionFillRequest) return undefined;

    const nextOwners = await getOwnersByStatus(
      positionFillRequest.status,
      getFullNameWithoutMaiden,
      searchAll,
      getLeadRecruiters,
    ).catch(() => {
      addMessage(new ErrorMessage('Could not load position fill request state owners.'));
    });

    return {
      positionFillRequest,
      stateOwners: nextOwners?.length ? nextOwners : [],
    };
  });

  return {
    positionFillRequest: data?.positionFillRequest,
    stateOwners: data?.stateOwners ?? [],
    ...state,
  };
};

export default usePositionFillRequestDetails;
